
import { lazy, Suspense } from "react";
import { lazyPage, installRoutePrefetch } from "@/lib/routePrefetch";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from '@/components/ErrorBoundary';
import NoIndexWrapper from '@/components/NoIndexWrapper';
import PageTransition from '@/components/PageTransition';
// Eager: homepage + critical
import PremiumIndex from "./pages/PremiumIndex";

// Kept out of the first load (2026-10-07): the 404 page still uses the old
// header (Radix menus, auth), and the toast stack (Radix toast) is only needed
// once a page raises a toast — toasts raised before it loads are kept by
// use-toast's store and shown when it mounts. The Sonner toaster and the root
// TooltipProvider were removed the same day: nothing used either.
const NotFound = lazy(() => import("./pages/NotFound"));
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((module) => ({ default: module.Toaster })),
);

// Lazy: everything else
const PremiumServices = lazyPage(() => import("./pages/premium/ServicesPage"));
const PremiumContact = lazyPage(() => import("./pages/premium/ContactPage"));
const PremiumAbout = lazyPage(() => import("./pages/premium/AboutPage"));
const PremiumFAQ = lazyPage(() => import("./pages/premium/FAQPage"));
const PremiumRepairStatus = lazyPage(() => import("./pages/premium/RepairStatusPage"));
const DevicePassport = lazyPage(() => import("./pages/premium/DevicePassportPage"));
const PremiumSubscriptions = lazyPage(() => import("./pages/premium/PremiumSubscriptions"));
const AccessibilityPage = lazyPage(() => import("./pages/premium/AccessibilityPage"));
const TermsPage = lazyPage(() => import("./pages/premium/TermsPage"));
const PrivacyPage = lazyPage(() => import("./pages/premium/PrivacyPage"));
const LabPage = lazyPage(() => import("./pages/premium/LabPage"));
const AuthPage = lazy(() => import("./pages/Auth"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPassword"));
const BookingPage = lazyPage(() => import("./pages/premium/BookingPage"));
const AdminPage = lazy(() => import("./pages/Admin"));
const AdminAppointmentsPage = lazy(() => import("./pages/AdminAppointments"));
const ProductManagementPage = lazy(() => import("./pages/ProductManagement"));
const AutoImportProducts = lazy(() => import("./pages/AutoImportProducts"));
const Shop = lazyPage(() => import("./pages/premium/ShopPage"));
const Diagnose = lazyPage(() => import("./pages/premium/DiagnosePage"));
const RetiredProduct = lazy(() => import("./pages/premium/RetiredProductPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYou"));
const LocalArea = lazyPage(() => import("./pages/LocalArea"));
const ShopProduct = lazyPage(() => import("./pages/premium/ShopProductPage"));
const ProductImport = lazy(() => import("./pages/ProductImport"));
const SmartProductUpload = lazy(() => import("./pages/SmartProductUpload"));
const BuildPC = lazyPage(() => import("./pages/premium/BuildPCPage"));
const BusinessLocal = lazyPage(() => import("./pages/premium/BusinessLocalPage"));
const Blog = lazyPage(() => import("./pages/premium/BlogPage"));
const BlogPost = lazyPage(() => import("./pages/premium/BlogPostPage"));
const Portfolio = lazyPage(() => import("./pages/premium/PortfolioPage"));
const PricingManager = lazy(() => import("./pages/PricingManager"));
const PricingProposal = lazy(() => import("./pages/PricingProposal"));
const ComputerRepairPrices = lazyPage(() => import("./pages/premium/ComputerRepairPricesPage"));
const LaptopScreen = lazyPage(() => import("./pages/premium/LaptopScreenPage"));
const SlowComputer = lazyPage(() => import("./pages/premium/SlowComputerPage"));
const TechnicianNorth = lazyPage(() => import("./pages/premium/TechnicianNorthPage"));
const TopicHub = lazyPage(() => import("./pages/premium/TopicHubPage"));

// Public routes whose code starts loading on hover/touch/focus of a link to them
// (see src/lib/routePrefetch.ts). Admin and auth pages are left out on purpose.
installRoutePrefetch([
  ["/services", PremiumServices.preload],
  ["/contact", PremiumContact.preload],
  ["/about", PremiumAbout.preload],
  ["/faq", PremiumFAQ.preload],
  ["/repair-status", PremiumRepairStatus.preload],
  ["/plans", PremiumSubscriptions.preload],
  ["/subscriptions", PremiumSubscriptions.preload],
  ["/maintenance", PremiumSubscriptions.preload],
  ["/accessibility", AccessibilityPage.preload],
  ["/terms", TermsPage.preload],
  ["/privacy", PrivacyPage.preload],
  ["/lab", LabPage.preload],
  ["/booking", BookingPage.preload],
  ["/shop", Shop.preload],
  ["/shop/:collection", Shop.preload],
  ["/diagnose", Diagnose.preload],
  ["/area/:area", LocalArea.preload],
  ["/product/:handle", ShopProduct.preload],
  ["/build-pc", BuildPC.preload],
  ["/business/local", BusinessLocal.preload],
  ["/blog", Blog.preload],
  ["/blog/:id", BlogPost.preload],
  ["/portfolio", Portfolio.preload],
  ["/guides/computer-repair-prices-israel", ComputerRepairPrices.preload],
  ["/laptop-screen-replacement", LaptopScreen.preload],
  ["/slow-computer", SlowComputer.preload],
  ["/computer-technician-north", TechnicianNorth.preload],
  ["/guides/laptop-buying", TopicHub.preload],
  ["/guides/computer-problems", TopicHub.preload],
  ["/guides/upgrades-and-gaming", TopicHub.preload],
]);

import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
// PWAInstallPrompt removed - unnecessary popup
import AccessibilityWidget from "./components/AccessibilityWidget";
import CookieConsent from "./components/CookieConsent";
import GeoGate from "./components/GeoGate";

const queryClient = new QueryClient();

const PageLoadingSpinner = () => (
  <div
    role="status"
    aria-label="טוען..."
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      width: '100%',
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
                <Suspense fallback={null}>
                  <Toaster />
                </Suspense>
                <BrowserRouter>
                  <GeoGate>
                  <a href="#main-content" className="skip-to-content">
                    דלג לתוכן הראשי
                  </a>
                  <PageTransition>
                  <Suspense fallback={<PageLoadingSpinner />}>
                  <Routes>
                  <Route path="/" element={<PremiumIndex />} />
                  <Route path="/premium" element={<PremiumIndex />} />
                  {/* Free consultation form (/diagnose) — the site's primary conversion action. The lab check itself is ₪150. */}
                  <Route path="/diagnose" element={<Diagnose />} />
                  <Route path="/about" element={<PremiumAbout />} />
                  <Route path="/services" element={<PremiumServices />} />
                  <Route path="/lab" element={<LabPage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/auth/confirm" element={<ResetPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/booking" element={<BookingPage />} />
                  {/* Booking confirmation (appointments), not an order receipt. */}
                  <Route path="/thank-you" element={<ThankYouPage />} />
                  <Route path="/my-appointments" element={<Navigate to="/" replace />} />
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/my-orders" element={<Navigate to="/" replace />} />
                  <Route path="/admin" element={<NoIndexWrapper><AdminPage /></NoIndexWrapper>} />
                  <Route path="/admin/appointments" element={<NoIndexWrapper><AdminAppointmentsPage /></NoIndexWrapper>} />
                  <Route path="/product-management" element={<NoIndexWrapper><ProductManagementPage /></NoIndexWrapper>} />
                  <Route path="/auto-import" element={<NoIndexWrapper><AutoImportProducts /></NoIndexWrapper>} />
                  <Route path="/product-import" element={<NoIndexWrapper><ProductImport /></NoIndexWrapper>} />
                  <Route path="/smart-upload" element={<NoIndexWrapper><SmartProductUpload /></NoIndexWrapper>} />
                  <Route path="/pricing-manager" element={<NoIndexWrapper><PricingManager /></NoIndexWrapper>} />
                  {/* Catalog: reference pages for gear we work with — no prices, no cart. */}
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/shop/:collection" element={<Shop />} />
                  <Route path="/catalog" element={<Navigate to="/shop" replace />} />
                  <Route path="/product/:handle" element={<ShopProduct />} />
                  <Route path="/accessibility" element={<AccessibilityPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                  <Route path="/contact" element={<PremiumContact />} />
                  <Route path="/build-pc" element={<BuildPC />} />
                  <Route path="/area/:area" element={<LocalArea />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  {/* Sevev (AI bot / secretary for businesses) removed 2026-10-14 (owner); old URLs → the lab's business page. */}
                  <Route path="/ai-bot" element={<Navigate to="/business/local" replace />} />
                  <Route path="/ai-secretary" element={<Navigate to="/business/local" replace />} />
                  <Route path="/business" element={<Navigate to="/business/local" replace />} />
                  <Route path="/business/local" element={<BusinessLocal />} />
                  <Route path="/faq" element={<PremiumFAQ />} />
                  <Route path="/proposal" element={<NoIndexWrapper><PricingProposal /></NoIndexWrapper>} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/repair-status" element={<PremiumRepairStatus />} />
                  <Route path="/d/:token" element={<NoIndexWrapper><DevicePassport /></NoIndexWrapper>} />
                  <Route path="/guides/computer-repair-prices-israel" element={<ComputerRepairPrices />} />
                  <Route path="/laptop-screen-replacement" element={<LaptopScreen />} />
                  <Route path="/slow-computer" element={<SlowComputer />} />
                  <Route path="/computer-technician-north" element={<TechnicianNorth />} />
                  <Route path="/guides/laptop-buying" element={<TopicHub />} />
                  <Route path="/guides/computer-problems" element={<TopicHub />} />
                  <Route path="/guides/upgrades-and-gaming" element={<TopicHub />} />
                  <Route path="/plans" element={<PremiumSubscriptions />} />
                  <Route path="/subscriptions" element={<PremiumSubscriptions />} />
                  <Route path="/maintenance" element={<PremiumSubscriptions />} />
                  {/* Retired sales surfaces — kept as redirects so old links and
                      search results land somewhere useful instead of a 404. */}
                  <Route path="/cart" element={<Navigate to="/shop" replace />} />
                  <Route path="/order/thank-you" element={<Navigate to="/" replace />} />
                  <Route path="/order/error" element={<Navigate to="/" replace />} />
                  <Route path="/gaming-bundle" element={<Navigate to="/shop" replace />} />
                  <Route path="/products" element={<Navigate to="/shop" replace />} />
                  {/* ~4,700 of these are still indexed and map to nothing. A noindex
                      tombstone gets them dropped; redirecting them all to /shop read
                      as a soft 404 at scale. */}
                  <Route path="/products/:id" element={<RetiredProduct />} />
                  {/* Retired classic (pre-cinematic) duplicates. */}
                  <Route path="/classic" element={<Navigate to="/" replace />} />
                  <Route path="/about-classic" element={<Navigate to="/about" replace />} />
                  <Route path="/services-classic" element={<Navigate to="/services" replace />} />
                  <Route path="/contact-classic" element={<Navigate to="/contact" replace />} />
                  <Route path="/faq-classic" element={<Navigate to="/faq" replace />} />
                  <Route path="/repair-status-classic" element={<Navigate to="/repair-status" replace />} />
                  <Route path="/plans-classic" element={<Navigate to="/plans" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                  </Suspense>
                  </PageTransition>
                {/* PWAInstallPrompt removed */}
                <AccessibilityWidget />
                <CookieConsent />
                  </GeoGate>
              </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
